import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { styled } from "styled-components";
import { Button } from "../components/common/Button";
import {
  ErrorMessage,
  EyeIcon,
  FormContainer,
  FormDivider,
  FormGroup,
  FormHeading,
  FormLink,
  FormText,
  Input,
  Label,
} from "../components/common/Form";
import { useAuthStore } from "../stores/auth.store";
import { AuthError } from "@supabase/supabase-js";

const PasswordContainer = styled.div`
  position: relative;
`;

const EyeIconButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AlertBanner = styled.div<{ $type: "error" | "warning" }>`
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 4px;
  background-color: ${({ $type }) =>
    $type === "error" ? "#FEE2E2" : "#FEF3C7"};
  border: 1px solid
    ${({ $type }) => ($type === "error" ? "#FCA5A5" : "#FCD34D")};
  color: ${({ $type }) => ($type === "error" ? "#B91C1C" : "#92400E")};
`;

type Inputs = {
  email: string;
  password: string;
};

const getErrorMessage = (error: AuthError) => {
  switch (error.code) {
    case "invalid_credentials":
      return "Email o password non corretti";
    case "email_not_confirmed":
      return "Email non confermata. Controlla la tua casella email per il link di conferma";
    default:
      return "Si è verificato un errore durante il login. Riprova più tardi";
  }
};

export const Login = () => {
  const { login } = useAuthStore();
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState<AuthError | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      setIsLoading(true);
      setLoginError(null);
      const responseError = await login({
        email: data.email,
        password: data.password,
      });

      if (responseError) {
        setLoginError(responseError);
        return;
      }

      navigate("/account");
    } catch (error) {
      console.error("Login failed:", error);
      setLoginError(error as AuthError);
    } finally {
      setIsLoading(false);
    }
  };

  const onTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <FormContainer>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormHeading>SEI REGISTRATO?</FormHeading>
          <FormText>
            INSERISCI LA TUA MAIL DI REGISTRAZIONE, PASSWORD E ACCEDI AL TUO
            ACCOUNT.
          </FormText>

          {loginError && (
            <AlertBanner $type="error">
              {getErrorMessage(loginError)}
            </AlertBanner>
          )}

          <FormGroup>
            <Label htmlFor="email">Mail</Label>
            <Input
              id="email"
              $hasError={!!errors.email}
              {...register("email", {
                required: "Campo obbligatorio",
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: "Inserisci un'email valida",
                },
              })}
            />
            {errors.email && (
              <ErrorMessage role="alert">{errors.email.message}</ErrorMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <PasswordContainer>
              <Input
                id="password"
                $hasError={!!errors.password}
                {...register("password", { required: "Campo obbligatorio" })}
                type={showPassword ? "text" : "password"}
              />
              <EyeIconButton
                type="button"
                onClick={onTogglePasswordVisibility}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <EyeIcon isVisible={showPassword} />
              </EyeIconButton>
            </PasswordContainer>
            {errors.password && (
              <ErrorMessage role="alert">
                {errors.password.message}
              </ErrorMessage>
            )}
          </FormGroup>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "24px",
              flexDirection: "column",
            }}
          >
            <span>Non ricordi la password?</span>
            <FormLink href="/forgot-password">Recupera la password</FormLink>
          </div>

          <Button type="submit" fullWidth disabled={isLoading}>
            {isLoading ? "ACCESSO IN CORSO..." : "ACCEDI"}
          </Button>
        </form>
      </FormContainer>
      <FormDivider />

      <FormContainer>
        <FormHeading>NON SEI REGISTRATO?</FormHeading>
        <FormText>CLICCA SUL BOTTONE E PROCEDI CON LA REGISTRAZIONE</FormText>
        <Button fullWidth onClick={() => navigate("/register")}>
          REGISTRATI
        </Button>
      </FormContainer>
    </>
  );
};
